import type { ReactNode } from "react";

import { cn } from "../../lib/cn";

type LabelSize = "large" | "medium" | "small" | "xsmall";
type LabelWeight = "regular" | "medium-bold" | "bold";
type LabelTransform = "none" | "uppercase";
type LabelColor =
    | "primary"
    | "inverse-primary"
    | "secondary"
    | "inverse-secondary"
    | "tertiary"
    | "highlight"
    | "error"
    | "warning"
    | "success"
    | "inactive"
    | "navigation-selected"
    | "navigation-unselected";

export type LabelProps = {
    className?: string;
    children: ReactNode;
    size?: LabelSize;
    weight?: LabelWeight;
    color?: LabelColor;
    /** Uppercase labels use their own letter-spacing token. */
    transform?: LabelTransform;
};

export function Label({
    className,
    children,
    size = "medium",
    weight = "regular",
    color = "primary",
    transform = "none",
}: LabelProps) {
    return (
        <span
            className={cn(
                labelBaseClass,
                labelSizeClass[size],
                labelWeightClass[weight],
                labelColorClass[color],
                labelTransformClass[transform],
                className,
            )}
        >
            {children}
        </span>
    );
}

const labelBaseClass = "m-0 whitespace-pre-wrap p-0 font-label";

const labelSizeClass: Record<LabelSize, string> = {
    large: "text-mobile-label-large leading-mobile-label-large sm-md:text-desktop-label-large sm-md:leading-desktop-label-large",
    medium: "text-mobile-label-medium leading-mobile-label-medium sm-md:text-desktop-label-medium sm-md:leading-desktop-label-medium",
    small: "text-mobile-label-small leading-mobile-label-small sm-md:text-desktop-label-small sm-md:leading-desktop-label-small",
    xsmall: "text-mobile-label-xsmall leading-mobile-label-xsmall sm-md:text-desktop-label-xsmall sm-md:leading-desktop-label-xsmall",
};

const labelColorClass: Record<LabelColor, string> = {
    primary: "text-text-primary",
    "inverse-primary": "text-text-inverse-primary",
    secondary: "text-text-secondary",
    "inverse-secondary": "text-text-inverse-secondary",
    tertiary: "text-text-tertiary",
    highlight: "text-text-highlight",
    error: "text-status-error",
    warning: "text-status-warning",
    success: "text-status-success",
    inactive: "text-status-inactive",
    "navigation-selected": "text-text-navigation-selected",
    "navigation-unselected": "text-text-navigation-unselected",
};

const labelTransformClass: Record<LabelTransform, string> = {
    none: "tracking-label-lowercase",
    uppercase: "uppercase tracking-label-uppercase",
};

const labelWeightClass: Record<LabelWeight, string> = {
    regular: "font-weight-label-regular",
    "medium-bold": "font-weight-label-medium font-weight-paragraph-medium",
    bold: "font-weight-label-bold",
};
